import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { useIdeaComposer } from '~publish/components/IdeaManagementRouter/hooks'
import { useAllChannelsUrl } from '~publish/components/MainSidebar/hooks/useAllChannelsUrl'
import { useCommentsUrl } from '~publish/components/MainSidebar/hooks/useCommentsUrl'
import { useIsLegacyPlan } from '~publish/contexts/AccountContext'
import { buildStartPageHomeUrl } from '~publish/helpers/externalUrls'
import { useHasAccessToBufferHome } from '~publish/hooks/useHasAccessToBufferHome'
import { usePostComposer } from '~publish/hooks/usePostComposer'

const SEQUENCE_TIMEOUT = 2000

/**
 * Extracts the current channel ID from the URL, supporting both
 * /channels/:id/... and /insights/:channelId patterns.
 */
function getCurrentChannelId(): string | null {
  const pathname = window.location.pathname
  const channelsMatch = pathname.match(/^\/channels\/([^/]+)/)
  if (channelsMatch) return channelsMatch[1]
  const insightsMatch = pathname.match(/^\/insights\/([^/]+)/)
  if (insightsMatch) return insightsMatch[1]
  return null
}

/**
 * Registers publish's global navigation/action keyboard shortcuts. These were
 * previously served by the app-shell KBar Launcher; publish now owns them so the
 * Launcher can be removed (CORE-3414) without breaking any documented shortcut.
 *
 * Two prefixed schemes, distinguished by Shift:
 *
 * Global navigation (unshifted — advertised in KeyboardShortcutsDialog):
 *   G P → all-channels queue            G S → Start Page (new tab)
 *   G A → Insights                      G I → Create / Ideas
 *   G C → all-channels Community        G H → Home (when Buffer Home enabled)
 *
 * Creation (unshifted — advertised in KeyboardShortcutsDialog):
 *   N P → new post composer   N I → new idea composer   N C → connect channel
 *
 * Channel-scoped navigation (Shift — undocumented power-user variants):
 *   G+Shift+P → current channel's queue     (fallback: all-channels)
 *   G+Shift+A → current channel's insights  (fallback: all-channels Insights)
 *   G+Shift+C → current channel's community (fallback: all-channels Community)
 *
 * Uses a capture-phase listener on window to fire before kbar's tinykeys handler.
 * While the app-shell Launcher still mounts, its KBarProvider also matches these
 * sequences; intercepting in capture phase and stopping propagation ensures
 * publish's destination wins. Once the Launcher is gone the stopPropagation is a
 * harmless no-op.
 *
 * Can't use useKeyboardShortcut for the Shift variants because it resets mid-sequence
 * when a standalone Shift keydown fires before the letter key.
 */
export function useNavigationShortcuts(): void {
  const navigate = useNavigate()
  const { hasAccess: hasAccessToHome } = useHasAccessToBufferHome()
  const isLegacyPlan = useIsLegacyPlan()

  // Pre-compute fallback URLs (used when no channel context is detected)
  const allChannelsUrl = useAllChannelsUrl()
  const commentsUrl = useCommentsUrl()

  const { createNewPostInComposer } = usePostComposer()
  const { createIdeaWithComposer } = useIdeaComposer()

  // Tracks the active prefix key (G or N) and when it was pressed; stored in a
  // ref to avoid triggering re-renders.
  const prefixRef = useRef<{ key: 'g' | 'n' | null; at: number }>({
    key: null,
    at: 0,
  })

  useEffect(() => {
    /** Returns true when the sequence matched and the event was handled. */
    function handleGoTo(event: KeyboardEvent): boolean {
      // Channel-scoped variants (Shift) — undocumented power-user shortcuts.
      if (event.shiftKey) {
        const channelId = getCurrentChannelId()
        if (event.key === 'P') {
          navigate(channelId ? `/channels/${channelId}` : allChannelsUrl)
          return true
        }
        if (event.key === 'A') {
          navigate(channelId ? `/insights/${channelId}` : '/insights')
          return true
        }
        if (event.key === 'C') {
          navigate(channelId ? `/channels/${channelId}/community` : commentsUrl)
          return true
        }
        return false
      }

      // Global navigation (unshifted) — advertised in KeyboardShortcutsDialog.
      switch (event.key) {
        case 'p':
          navigate(allChannelsUrl)
          return true
        case 'a':
          navigate('/insights')
          return true
        case 'c':
          navigate(commentsUrl)
          return true
        case 's':
          window.open(buildStartPageHomeUrl(), '_blank', 'noopener')
          return true
        case 'i':
          navigate('/create/ideas')
          return true
        case 'h':
          if (!hasAccessToHome) return false
          navigate('/home')
          return true
        default:
          return false
      }
    }

    /** Returns true when the sequence matched and the event was handled. */
    function handleNew(event: KeyboardEvent): boolean {
      switch (event.key) {
        case 'p':
          void createNewPostInComposer({
            channels: 'all',
            cta: 'publish-keyboardShortcut-newPost-np-1',
          })
          return true
        case 'i':
          if (isLegacyPlan) return false
          createIdeaWithComposer({
            source: 'publish-keyboardShortcut-newIdea-ni-1',
          })
          return true
        case 'c':
          navigate('/settings/channels')
          return true
        default:
          return false
      }
    }

    function handleKeyDown(event: KeyboardEvent): void {
      const target = event.target
      const inInput =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      if (inInput || event.ctrlKey || event.metaKey || event.altKey) {
        prefixRef.current = { key: null, at: 0 }
        return
      }

      // Record an unshifted prefix key (G or N) and wait for the second key.
      if (!event.shiftKey && (event.key === 'g' || event.key === 'n')) {
        prefixRef.current = { key: event.key, at: Date.now() }
        return
      }

      // Ignore standalone modifier keys — Shift fires its own keydown before
      // the letter key, so we must not reset state when we see it.
      if (
        event.key === 'Shift' ||
        event.key === 'Control' ||
        event.key === 'Alt' ||
        event.key === 'Meta'
      ) {
        return
      }

      const prefix = prefixRef.current
      const prefixActive =
        prefix.key !== null && Date.now() - prefix.at < SEQUENCE_TIMEOUT

      if (prefixActive) {
        const handled =
          prefix.key === 'g' ? handleGoTo(event) : handleNew(event)
        if (handled) {
          // Stop the event from reaching kbar's tinykeys, which also matches
          // the lowercased sequence and would navigate to a stale destination.
          event.stopPropagation()
          event.preventDefault()
        }
      }

      prefixRef.current = { key: null, at: 0 }
    }

    // Capture phase fires before kbar's tinykeys bubble-phase listener
    window.addEventListener('keydown', handleKeyDown, true)
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [
    navigate,
    hasAccessToHome,
    isLegacyPlan,
    allChannelsUrl,
    commentsUrl,
    createNewPostInComposer,
    createIdeaWithComposer,
  ])
}
